import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

// API/Components
import axiosInstance from '../components/axios';
import useLocationGraphConfig from '../components/hooks/useLocationGraphConfig';
import useHotkeyHandlers, { HOTKEY_NAV } from '../components/hooks/useHotkeyHandlers';
import DataViewSwitch from '../components/data_view_switch';
import StringDischargeTrendReport from '../components/string_discharge_trend_report';
import StringDischargeTrendGraph from '../components/string_discharge_trend_graph';
import { parseDateTime, formatDateTime } from '../components/enums';

// Material UI
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';


// String Trend sub-page rendering (Location Name, Title, String Selection)
export default function StringDischargeTrend() {
	const { locationNum, eventDateTime } = useParams();
	const [ searchParams ] = useSearchParams();

	// State Management
	const [selectedString, setSelectedString] = useState(1);

	const [trendData, setTrendData] = useState({});
	const { locationInfo, limitData, graphScalesData, isLoading, setIsLoading } = useLocationGraphConfig({
		locationNum,
		limitsEndpoint: `/loc/${locationNum}/limits/discharge/string`,
		scalesEndpoint: `/loc/${locationNum}/graph_scales`
	});
	
	const [graphDisplay, setGraphDisplay] = useState(false);
	const [graphType, setGraphType] = useState(0);
	
	// Check if current measurement is disabled or set to system-measured only from limits data
	const noCurrentMeasurement = (limitData?.no_current_measurement ?? false) || !(limitData?.current_per_string ?? true);
	
	const numStringsRef = useRef();
	const selectedStringRef = useRef();
	const graphTypeRef = useRef();
	const eventDateObj = parseDateTime(eventDateTime);

	// Helpers to check if trendData is empty and if DataViewSwitch should be disabled
	const isTrendDataEmpty = !trendData || (Object.keys(trendData).length === 0);
	const isDataSwitchDisabled = isLoading || isTrendDataEmpty;
	const dataSwitchDisabledText = isTrendDataEmpty ? "No data available to graph" : "Data is loading...";

	// Handle the current selectedString for hotkey nav
	useEffect(() => {
		numStringsRef.current = locationInfo.num_strings;
	}, [locationInfo]);

	// Handle the current selectedString for hotkey nav
	useEffect(() => {
		selectedStringRef.current = selectedString;
	}, [selectedString]);

	// Handle the current graphType for hotkey nav
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);

	// Handle keypresses for string and graph navigation
	useHotkeyHandlers([
		{
			key: 'PageUp',
			down: (e) => {
				e.preventDefault();  // Prevent default scrolling behavior
				handleStringNavigation(HOTKEY_NAV.BACK);
			},
			up: () => {},
		},
		{
			key: 'PageDown',
			down: (e) => {
				e.preventDefault();
				handleStringNavigation(HOTKEY_NAV.FORWARD);
			},
			up: () => {},
		},
		{
			key: 'F8',
			down: (e) => {
				e.preventDefault();
				if (!noCurrentMeasurement) {
					switch(graphTypeRef.current) {
						case 0:  // Voltage -> Current
							setGraphType(1);
							break;
						case 1:  // Current -> Voltage
							setGraphType(0);
							break;
						default:
							setGraphType(0);
							break;
					}
				}
			},
			up: () => {},
		},
	]);

	// Handle string navigation
	function handleStringNavigation(stringNav) {
		const numStrings = numStringsRef.current;
		const stringNum = selectedStringRef.current;

		if (selectedStringRef.current > 0) {
			if (stringNav === HOTKEY_NAV.BACK && stringNum > 1) {  // Decrement string
				setSelectedString(Number(stringNum - 1));
			}

			if (stringNav === HOTKEY_NAV.FORWARD && stringNum < numStrings) {  // Increment string
				setSelectedString(Number(stringNum + 1));
			}
		}
	}
	
	// Set the selected string from the URL parameter
	useEffect(() => {
		setSelectedString(Number(searchParams.get("string")) || 1);
	}, [locationNum, searchParams]);
	
	// Get the list of trend data for the specified location id, string number, and unit number
	useEffect(() => {
		if (!selectedString) return;

		setIsLoading(true);

		// Get String Trend Data
		axiosInstance.get(`/loc/${locationNum}/discharges/${eventDateTime}/string_${selectedString}`)
			.then(response => {
				setTrendData(response.data);
			})
			.catch(error => {
				console.error("Error fetching discharge trend data:", error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [locationNum, eventDateTime, selectedString]);

	// Event Handlers
	const handleGraphTypeChange = (event) => {
		setGraphType(Number(event.target.value));
	}
	
	const handleStringChange = (event) => {
		setSelectedString(Number(event.target.value));
	}
	
	const handleViewChange = (event) => {
		setGraphDisplay(event.target.checked);
	}
		
	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				{ locationInfo.location_name } (SN {locationInfo.serial_number})
			</Typography>
			<Toolbar>
				<Grid container spacing={1} alignItems="center">
					<Grid item xs alignItems="left">
						<Typography
							align="left"
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ flexGrow: 1}}
						>
							String {selectedString} Discharge Trend : {formatDateTime(eventDateObj)}
						</Typography>
					</Grid>
					{graphDisplay && !noCurrentMeasurement &&
						<Grid item>
							<FormControl sx={{ m: 1, minWidth: 100 }}>
								<label id="graph-type-select" style={{fontFamily:"Saira, sans-serif"}}>Graph Type</label>
								<select
									id="graph-type-select"
									value={graphType}
									label="Graph Type"
									onChange={handleGraphTypeChange}
								>
									<option key={0} value={0}>Voltage</option>
									<option key={1} value={1}>Current</option>
								</select>
							</FormControl>
						</Grid>
					}
					<Grid item>
						<FormControl sx={{ m: 0, minWidth: 80 }}>
							<label id="string-select" style={{fontFamily:"Saira, sans-serif"}}>String</label>
							<select
								id="string-select"
								value={selectedString}
								label="String"
								onChange={handleStringChange}
								sx= {{ maxHeight: 500 }}
							>
								{ Array.from({length: locationInfo.num_strings}, (_, i) => i + 1).map((stringNum) => (
									<option key={stringNum} value={stringNum}>String {stringNum}</option>
								)) }
							</select>
						</FormControl>
					</Grid>
					<Grid item>
						<DataViewSwitch
							checked={graphDisplay}
							onChange={handleViewChange}
							leftText="Report"
							rightText="Graph"
							disabled={isDataSwitchDisabled}
							disabledText={dataSwitchDisabledText}
						/>
					</Grid>
				</Grid>
			</Toolbar>
			{ graphDisplay ?
				<StringDischargeTrendGraph
					trendData={trendData}
					limitData={limitData}
					graphScalesData={graphScalesData}
					selectedString={selectedString}
					graphType={graphType}
					loading={isLoading}
					noCurrentMeasurement={noCurrentMeasurement}
				/>
			:
				<StringDischargeTrendReport
					trendData={trendData}
					loading={isLoading}
					noCurrentMeasurement={noCurrentMeasurement}
				/>
			}
		</Paper>
	);
}